import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Camera, Smartphone, Upload } from 'lucide-react';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import { renderGuestQrDataUrl } from '../../shared/guestQrCode';
import {
  buildMobileUploadPageUrl,
  claimMobileUploadImage,
  createMobileUploadSession,
} from '../../looks/utils/looksApi';
import { getSalonAppSessionToken } from '../../salon/utils/salonAppSessionToken';
import { getFranchiseId } from '../../shared/franchiseFlowAuth';
import {
  readStoredFranchiseBrandLogo,
  readStoredFranchiseBrandName,
} from '../../shared/franchiseBrand';
import { shouldShowKioskPoweredBy } from '../../shared/kioskPoweredBy';
import '../styles/StudioPhotoSourceModal.css';

export default function StudioPhotoSourceModal({ open, onClose, onImageReady }) {
  const navigate = useNavigate();
  const { appPath } = useBeautyProduct();
  const titleId = useId();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('pick');
  const [phoneToken, setPhoneToken] = useState('');
  const [phoneQr, setPhoneQr] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const reset = useCallback(() => {
    setPhase('pick');
    setPhoneToken('');
    setPhoneQr('');
    setPhoneBusy(false);
    setPhoneError('');
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const closeAll = useCallback(() => {
    reset();
    onClose?.();
  }, [onClose, reset]);

  const startPhoneQr = useCallback(async () => {
    setPhase('phone');
    setPhoneBusy(true);
    setPhoneError('');
    setPhoneQr('');
    setPhoneToken('');
    try {
      const session = await createMobileUploadSession({
        branding: {
          logo: readStoredFranchiseBrandLogo(),
          name: readStoredFranchiseBrandName(),
          powered_by: shouldShowKioskPoweredBy(),
        },
      });
      const pageUrl = buildMobileUploadPageUrl(session.pagePath, {
        appSessionToken: getSalonAppSessionToken(),
        franchiseId: getFranchiseId(),
      });
      const qr = await renderGuestQrDataUrl(pageUrl, 260);
      setPhoneToken(session.token);
      setPhoneQr(qr);
    } catch (err) {
      setPhoneError(err.message || 'Could not start phone upload');
    } finally {
      setPhoneBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open || phase !== 'phone' || !phoneToken) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const claimed = await claimMobileUploadImage(phoneToken);
        if (cancelled) return;
        if (claimed.status === 'waiting' || !claimed.image) return;
        onImageReady?.(claimed.image);
        closeAll();
      } catch {
        /* poll */
      }
    };
    const id = window.setInterval(() => { void tick(); }, 2000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [open, phase, phoneToken, onImageReady, closeAll]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        onImageReady?.(reader.result);
        closeAll();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="studio-photo-modal" role="presentation" onClick={closeAll}>
      <div
        className="studio-photo-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="studio-photo-modal__close" aria-label="Close" onClick={closeAll}>
          ×
        </button>
        <h3 id={titleId}>Add your photo</h3>
        {phase === 'pick' ? (
          <>
            <p className="studio-photo-modal__lede">Camera, gallery, or scan QR on your phone.</p>
            <div className="studio-photo-modal__options">
              <button
                type="button"
                className="studio-photo-modal__option"
                onClick={() => {
                  closeAll();
                  navigate(appPath('/capture/preview/camera'));
                }}
              >
                <Camera size={22} aria-hidden />
                <span>Camera</span>
              </button>
              <button
                type="button"
                className="studio-photo-modal__option"
                onClick={() => fileRef.current?.click()}
              >
                <Upload size={22} aria-hidden />
                <span>Upload</span>
              </button>
              <button type="button" className="studio-photo-modal__option" onClick={() => { void startPhoneQr(); }}>
                <Smartphone size={22} aria-hidden />
                <span>Phone QR</span>
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="studio-photo-modal__file"
              onChange={onFileChange}
            />
          </>
        ) : (
          <>
            <p className="studio-photo-modal__lede">Scan with your phone to capture or upload.</p>
            <div className="studio-photo-modal__qr">
              {phoneBusy ? <span>Preparing…</span> : phoneQr ? (
                <img src={phoneQr} alt="QR code for phone upload" />
              ) : (
                <span>{phoneError || 'Link unavailable'}</span>
              )}
            </div>
            <button type="button" className="studio-photo-modal__back" onClick={() => setPhase('pick')}>
              Back
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
